import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

import commonRu from "./ru/common.json";
import onboardingRu from "./ru/onboarding.json";
import homeRu from "./ru/home.json";
import assetRu from "./ru/asset.json";
import exchangeRu from "./ru/exchange.json";
import profileRu from "./ru/profile.json";
import referralRu from "./ru/referral.json";
import historyRu from "./ru/history.json";
import cardsRu from "./ru/cards.json";
import depositRu from "./ru/deposit.json";
import withdrawRu from "./ru/withdraw.json";
import errorsRu from "./ru/errors.json";
import lockRu from "./ru/lock.json";
import payqrRu from "./ru/payqr.json";
import aboutRu from "./ru/about.json";
import faqRu from "./ru/faq.json";
import betaRu from "./ru/beta.json";
import webAuthRu from "./ru/web-auth.json";

import commonEn from "./en/common.json";
import onboardingEn from "./en/onboarding.json";
import homeEn from "./en/home.json";
import assetEn from "./en/asset.json";
import exchangeEn from "./en/exchange.json";
import profileEn from "./en/profile.json";
import referralEn from "./en/referral.json";
import historyEn from "./en/history.json";
import cardsEn from "./en/cards.json";
import depositEn from "./en/deposit.json";
import withdrawEn from "./en/withdraw.json";
import errorsEn from "./en/errors.json";
import lockEn from "./en/lock.json";
import payqrEn from "./en/payqr.json";
import aboutEn from "./en/about.json";
import faqEn from "./en/faq.json";
import betaEn from "./en/beta.json";
import webAuthEn from "./en/web-auth.json";

export const SUPPORTED_LANGS = ["ru", "en"] as const;
export type SupportedLang = (typeof SUPPORTED_LANGS)[number];

const LS_KEY = "zero_language";

// Пытаемся вытащить язык из Telegram initData, если WebApp
// открыт внутри Telegram. Русскоязычным юзерам («ru») по умолчанию
// показываем русский, всем остальным (uk/be/kk/…, en/…) — английский:
// проще и честнее, чем автопереводить всё подряд.
function detectFromTelegram(): SupportedLang | null {
  try {
    const tg = (window as unknown as { Telegram?: { WebApp?: { initDataUnsafe?: { user?: { language_code?: string } } } } }).Telegram?.WebApp;
    const code = tg?.initDataUnsafe?.user?.language_code;
    if (!code) return null;
    const lang = code.slice(0, 2).toLowerCase();
    if ((SUPPORTED_LANGS as readonly string[]).includes(lang)) {
      return lang as SupportedLang;
    }
    return "en";
  } catch {
    return null;
  }
}

function detectInitialLanguage(): SupportedLang {
  // 1) Что уже выбрано пользователем — приоритет.
  try {
    const stored = localStorage.getItem(LS_KEY);
    if (stored && (SUPPORTED_LANGS as readonly string[]).includes(stored)) {
      return stored as SupportedLang;
    }
  } catch {
    /* no-op — SSR/приват-режим/квота */
  }
  // 2) Telegram initData.
  const fromTg = detectFromTelegram();
  if (fromTg) return fromTg;
  // 3) Браузер.
  try {
    const nav = navigator.language?.slice(0, 2).toLowerCase();
    if (nav === "ru") return "ru";
  } catch {
    /* no-op */
  }
  return "en";
}

export const initialLanguage = detectInitialLanguage();

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      ru: {
        common: commonRu,
        onboarding: onboardingRu,
        home: homeRu,
        asset: assetRu,
        exchange: exchangeRu,
        profile: profileRu,
        referral: referralRu,
        history: historyRu,
        cards: cardsRu,
        deposit: depositRu,
        withdraw: withdrawRu,
        errors: errorsRu,
        lock: lockRu,
        payqr: payqrRu,
        about: aboutRu,
        faq: faqRu,
        beta: betaRu,
        "web-auth": webAuthRu,
      },
      en: {
        common: commonEn,
        onboarding: onboardingEn,
        home: homeEn,
        asset: assetEn,
        exchange: exchangeEn,
        profile: profileEn,
        referral: referralEn,
        history: historyEn,
        cards: cardsEn,
        deposit: depositEn,
        withdraw: withdrawEn,
        errors: errorsEn,
        lock: lockEn,
        payqr: payqrEn,
        about: aboutEn,
        faq: faqEn,
        beta: betaEn,
        "web-auth": webAuthEn,
      },
    },
    lng: initialLanguage,
    fallbackLng: "en",
    supportedLngs: SUPPORTED_LANGS as unknown as string[],
    ns: [
      "common",
      "onboarding",
      "home",
      "asset",
      "exchange",
      "profile",
      "referral",
      "history",
      "cards",
      "deposit",
      "withdraw",
      "errors",
      "lock",
      "payqr",
      "about",
      "faq",
      "beta",
      "web-auth",
    ],
    defaultNS: "common",
    interpolation: { escapeValue: false },
    returnNull: false,
    detection: {
      // Мы сами всё определили выше — детектор оставим на всякий случай,
      // но кэш пишем сами в setLanguage().
      order: ["localStorage", "navigator"],
      lookupLocalStorage: LS_KEY,
      caches: [],
    },
  });

/** Установить язык явно (сохраняется в localStorage, синкается с i18next). */
export function setLanguage(lang: SupportedLang) {
  try {
    localStorage.setItem(LS_KEY, lang);
  } catch {
    /* no-op */
  }
  void i18n.changeLanguage(lang);
  try {
    document.documentElement.lang = lang;
  } catch {
    /* no-op */
  }
}

/** Текущий язык (`ru` | `en`). */
export function getCurrentLanguage(): SupportedLang {
  const lng = (i18n.resolvedLanguage || i18n.language || initialLanguage) as string;
  const two = lng.slice(0, 2).toLowerCase();
  return (SUPPORTED_LANGS as readonly string[]).includes(two)
    ? (two as SupportedLang)
    : "en";
}

// Проставим <html lang> при старте.
try {
  document.documentElement.lang = initialLanguage;
} catch {
  /* no-op */
}

export default i18n;
